import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";

import ChevronLeftIcon from "../Icons/ChevronLeftIcon";
import ChevronRightIcon from "../Icons/ChevronRightIcon";
import TickIcon from "../../assets/images/icons/tick-icon.svg";
import DashIcon from "../../assets/images/icons/dash-icon.svg";
import EmptyStatistics from "../../assets/images/icons/empty-stats.svg";

/**
 * ClubbieTable - JSX component with Tailwind CSS
 * Advanced data table with TanStack Table integration
 * 
 * Features:
 * - Pagination with configurable page sizes
 * - Optional row selection (single/multi)
 * - Empty state with custom messages
 * - Responsive design with alternating row colors
 * - Custom column rendering via flexRender
 * 
 * @param {array} data - Table data array
 * @param {array} columns - Column definitions
 * @param {string} title - Table title (unused in current UI)
 * @param {number} pageSize - Initial page size (default: 10)
 * @param {string} className - Additional CSS classes
 * @param {boolean} showPageSize - Show page size selector
 * @param {string} noDataText - Empty state message
 * @param {boolean} enableSelection - Enable row selection
 * @param {function} onSelectionChange - Selection change callback
 */

const ClubbieTable = ({
  data,
  columns,
  title,
  pageSize = 10,
  className = "",
  showPageSize = true,
  noDataText = "No data found",
  enableSelection = false, // New prop to enable/disable selection
  onSelectionChange, // Callback for selection changes
}) => {
  
  // Add checkbox column if selection is enabled
  const columnsWithSelection = React.useMemo(() => {
    if (!enableSelection) return columns;

    const checkboxColumn = {
      id: 'select',
      header: ({ table }) => (
        <div className="flex justify-center items-center">
          <button
            className={`w-5 h-5 border-2 rounded flex items-center justify-center cursor-pointer transition-colors ${
              table.getIsAllRowsSelected() ? 'border-[#00E7FF] bg-[#00E7FF]' : 'border-[#333E48] hover:border-[#00E7FF]'
            }`}
            onClick={table.getToggleAllRowsSelectedHandler()}
            type="button"
          >
            {table.getIsAllRowsSelected() ? (
              <img src={DashIcon} alt="deselect" className="w-3 h-3" />
            ) : table.getIsSomeRowsSelected() ? (
              <img src={DashIcon} alt="partial" className="w-3 h-3" />
            ) : null}
          </button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center items-center">
          <button
            className={`w-5 h-5 border-2 rounded flex items-center justify-center cursor-pointer transition-colors ${
              row.getIsSelected() ? 'border-[#00E7FF] bg-[#00E7FF]' : 'border-[#333E48] hover:border-[#00E7FF]'
            }`}
            onClick={row.getToggleSelectedHandler()}
            type="button"
          >
            {row.getIsSelected() ? (
              <img src={TickIcon} alt="selected" className="w-3 h-3" />
            ) : null}
          </button>
        </div>
      ),
      size: 50,
      enableSorting: false,
      enableResizing: false,
    };

    return [checkboxColumn, ...columns];
  }, [columns, enableSelection]);

  const table = useReactTable({
    data: data || [],
    columns: columnsWithSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: enableSelection,
    getRowId: (row, index) => row.id || index.toString(),
    onRowSelectionChange: (updater) => {
      // Handle selection changes
      const newSelection = typeof updater === 'function' 
        ? updater(table.getState().rowSelection) 
        : updater;
      
      if (onSelectionChange) {
        const selectedRows = Object.keys(newSelection).map(id => 
          data[parseInt(id)]
        );
        onSelectionChange(selectedRows, newSelection);
      }
    },
    initialState: {
      pagination: {
        pageSize,
        pageIndex: 0,
      },
      rowSelection: {},
    },
  });

  return (
    <>
      <div className="bg-[#000F1F] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className={`w-full ${className}`}>
            <thead className="bg-[#041A30] border-b border-[#192634]">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-4 text-left text-sm font-semibold text-[#FEFCEB] uppercase "
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-[#192634]">
              {!data || data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnsWithSelection.length}
                    className="text-center py-12"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={EmptyStatistics}
                        alt="No results"
                        className="w-[150px] mb-4"
                      />
                      <p className="text-[#FEFCEB] text-sm m-0">
                        {(() => {
                          const text = typeof noDataText === "function" ? noDataText() : noDataText;
                          return text ? `No matches found${text}` : "No available tables";
                        })()}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`transition-colors ${
                      index % 2 === 0 ? "bg-[#041A30]" : "bg-[#000F1F]"
                    } hover:bg-[#082441]`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 text-sm text-[#E5E5D6]">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Show selected row count */}
      {enableSelection && table.getSelectedRowModel().rows.length > 0 && (
        <div className="px-6 py-3 bg-[#041A30] border-t border-[#192634]">
          <span className="text-sm text-[#7F8585]">
            {table.getSelectedRowModel().rows.length} of {data?.length || 0} rows selected
          </span>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="flex justify-end items-center gap-4 px-6 py-4 bg-[#041A30] border-t border-[#192634]">
          {showPageSize && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#7F8585]">Rows per page:</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                className="px-3 py-1.5 bg-[#021529] border border-[#1a3a5c] rounded-lg text-[#E5E5D6] text-sm outline-none cursor-pointer hover:border-[#2196F3] focus:border-[#00E7FF] transition-colors"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="w-9 h-9 flex items-center justify-center bg-[#082441] rounded-lg text-[#FEFCEB] hover:bg-[#0a3d62] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon />
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="w-9 h-9 flex items-center justify-center bg-[#082441] rounded-lg text-[#FEFCEB] hover:bg-[#0a3d62] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ClubbieTable;